<script lang="ts">
  /**
   * The on-screen warning for a provider the last run skipped.
   *
   * A skipped provider's rows are never hidden - they stay in the table, dimmed - so the warning
   * is what tells the reviewer that those rows are from an earlier run and carries the reason the
   * run gave (spec 6.2).
   */
  import { describeFreshness, type ExposedDocumentsProviderMetadata } from "../exposedSensitiveDocuments";
  import { formatPublishedDate } from "./publishedDate";

  let {
    metadata,
    now = new Date(),
  }: {
    /** Only the skipped providers whose rows are on screen; the caller does that filtering. */
    metadata: ExposedDocumentsProviderMetadata[];
    now?: Date;
  } = $props();

  const warnings = $derived(
    metadata.map((meta) => ({
      id: meta.id,
      freshness: describeFreshness(meta, now),
      publishedOn: formatPublishedDate(meta.publishedAt),
    })),
  );
</script>

{#each warnings as warning (warning.id)}
  {#if warning.freshness.status === "skipped"}
    <div
      class="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
      data-testid="skipped-provider-warning"
      role="status"
    >
      <span class="font-semibold">{warning.freshness.providerLabel} was skipped by the last run</span>
      — {warning.freshness.reason}. Its rows below are dimmed and are from the previous run{warning.publishedOn
        ? ` on ${warning.publishedOn}`
        : ""}, so they may no longer be accurate.
    </div>
  {/if}
{/each}
